import { tr, usePartnerTranslation } from "../../../lib/language";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Switch } from "../../ui/switch";
import { Checkbox } from "../../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../ui/dialog";
import { cn } from "../../../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ROLES = [
  {
    value: "administration",
    get label() { return tr("Administration"); },
    get description() { return tr("Has access to everything. Default page: Home."); },
  },
  {
    value: "finance",
    get label() { return tr("Finance"); },
    get description() { return tr("Can access the Finance tab and View My Shop only. Default page: Finance."); },
  },
  {
    value: "operations",
    get label() { return tr("Operations"); },
    get description() { return tr("Can access Home (without the revenue panel), Create, Bookings, Products and My Shop with all features. Default page: Home."); },
  },
];

export const NOTIFICATION_CHANNELS = [
  { value: "customer_questions", get label() { return tr("Customer questions"); } },
  { value: "bookings", get label() { return tr("Bookings"); } },
  { value: "accounting", get label() { return tr("Accounting"); } },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_ALLOWED_RE = /^[+0-9()\-\s.]*$/;

const normaliseAccountPhone = (value = "") =>
  String(value).replace(/[^+0-9()\-\s.]/g, "").slice(0, 30);

const Field = ({
  label,
  optional,
  error,
  children
}) => {
  usePartnerTranslation();
  return <div className="space-y-1">
    <label className="flex items-baseline justify-between text-xs font-semibold text-[#0F1115]">
      <span>{label}</span>
      {optional && <span className="text-[10px] font-normal text-[#9BA1AB]">{tr("Optional")}</span>}
    </label>
    {children}
    {error && <p className="text-[11px] font-medium text-[#9B0000]">{error}</p>}
  </div>;
};

const SectionLabel = ({
  children,
  subtitle,
  right
}) => {
  usePartnerTranslation();
  return <div className="flex items-start justify-between gap-3">
    <div>
      <h3 className="text-sm font-extrabold tracking-tight text-[#0F1115]">{children}</h3>
      {subtitle && <p className="mt-0.5 text-xs text-[#6B7280]">{subtitle}</p>}
    </div>
    {right}
  </div>;
};

const AddUserModal = ({ open, onClose, onCreated, editingUser = null }) => {
  usePartnerTranslation();
  const isEdit = !!editingUser;
  const editingOwner = !!editingUser?.is_owner;
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    job_title: "",
    phone: "",
    allow_login: false,
    role: "operations",
    receive_notifications: false,
    notification_prefs: [],
    marketing_emails: false,
  });

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      editingUser
        ? {
            email: editingUser.email || "",
            full_name: editingUser.full_name || "",
            job_title: editingUser.job_title || "",
            phone: editingUser.phone || "",
            allow_login: editingUser.is_owner ? true : !!editingUser.allow_login,
            role: editingUser.is_owner ? "administration" : editingUser.role || "operations",
            receive_notifications: !!editingUser.receive_notifications,
            notification_prefs: editingUser.notification_prefs || [],
            marketing_emails: !!editingUser.marketing_emails,
          }
        : {
            email: "",
            full_name: "",
            job_title: "",
            phone: "",
            allow_login: false,
            role: "operations",
            receive_notifications: false,
            notification_prefs: [],
            marketing_emails: false,
          },
    );
  }, [open, editingUser]);

  const set = (key, value) => {
    if (editingOwner && key === "allow_login" && !value) return;
    if (editingOwner && key === "role" && value !== "administration") return;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => {
      if (!e[key]) return e;
      const { [key]: _, ...rest } = e;
      return rest;
    });
  };

  const toggleChannel = (channel) => {
    set(
      "notification_prefs",
      form.notification_prefs.includes(channel)
        ? form.notification_prefs.filter((c) => c !== channel)
        : [...form.notification_prefs, channel],
    );
  };

  const validate = () => {
    const e = {};
    if (!form.email || !EMAIL_REGEX.test(form.email))
      e.email = tr("Enter a valid email address");
    if (form.phone && (!PHONE_ALLOWED_RE.test(form.phone) || !/\d/.test(form.phone)))
      e.phone = tr("Use only numbers, spaces, +, -, ., and parentheses.");
    if (form.allow_login && !form.role) e.role = tr("Pick a role for this user");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = editingOwner
        ? { ...form, allow_login: true, role: "administration" }
        : form;
      if (isEdit) {
        await axios.patch(`${API}/account/users/${editingUser.id}`, payload);
        toast.success(tr("User updated"));
      } else {
        await axios.post(`${API}/account/users`, payload);
        toast.success(tr("User invited"));
      }
      onCreated?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong.";
      if (err?.response?.status === 409) setErrors({ email: msg });
      else toast.error(tr("Couldn't save user"), { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent
        data-testid="add-user-modal"
        className="max-w-[560px] gap-0 rounded-2xl border-[#EFE4E4] p-0 sm:max-w-[600px]"
      >
        <DialogHeader className="border-b border-[#EFE4E4] px-5 py-4 sm:px-6">
          <DialogTitle className="text-base font-extrabold tracking-tight text-[#0F1115] sm:text-lg">
            {isEdit ? tr("Edit user profile") : tr("Add a new user profile")}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">{tr("Invite a teammate to help manage your products and bookings.")}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          <Field label={tr("Email")} error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="teammate@example.com"
              disabled={isEdit}
              data-testid="add-user-email"
              className={cn(
                "humrahe-input",
                isEdit && "cursor-not-allowed bg-[#FAFAFB] text-[#9BA1AB]",
                errors.email && "border-[#9B0000]",
              )}
            />
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={tr("Full name")} optional>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                placeholder={tr("Jane Doe")}
                data-testid="add-user-full-name"
                className="humrahe-input"
              />
            </Field>
            <Field label={tr("Job title")} optional>
              <input
                type="text"
                value={form.job_title}
                onChange={(e) => set("job_title", e.target.value)}
                placeholder={tr("Operations manager")}
                data-testid="add-user-job-title"
                className="humrahe-input"
              />
            </Field>
            <Field label={tr("Phone number")} optional error={errors.phone}>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => set("phone", normaliseAccountPhone(e.target.value))}
                placeholder="+1 555 123 4567"
                data-testid="add-user-phone"
                className={cn("humrahe-input", errors.phone && "border-[#9B0000]")}
              />
            </Field>
          </div>

          {/* Login & role */}
          <div className="space-y-3 border-t border-[#F0F2F5] pt-4">
            <SectionLabel
              subtitle={tr("Allow this user to sign in and pick what they can access.")}
              right={
                <Switch
                  checked={form.allow_login}
                  onCheckedChange={(v) => set("allow_login", !!v)}
                  disabled={editingOwner}
                  data-testid="add-user-allow-login"
                />
              }
            >{tr("Login access and role")}</SectionLabel>

            {form.allow_login && (
              <div className="space-y-2 pt-1" data-testid="add-user-role-list">
                {ROLES.map((r) => {
                  const active = form.role === r.value;
                  const disabled = editingOwner && r.value !== "administration";
                  return (
                    <button
                      type="button"
                      key={r.value}
                      onClick={() => !disabled && set("role", r.value)}
                      disabled={disabled}
                      data-testid={`add-user-role-${r.value}`}
                      aria-disabled={disabled}
                      aria-pressed={active}
                      className={cn(
                        "flex w-full items-start gap-3 rounded-lg border bg-white px-3.5 py-2.5 text-start transition-colors",
                        active
                          ? "border-[#FF5757] bg-[#FFFAFA]"
                          : "border-[#F0F2F5] hover:border-[#CFD2D8]",
                        disabled &&
                          "cursor-not-allowed opacity-45 hover:border-[#F0F2F5]",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-1 inline-block h-3.5 w-3.5 flex-shrink-0 rounded-full border",
                          active
                            ? "border-[#FF5757] bg-[#FF5757] ring-2 ring-white"
                            : "border-[#CFD2D8]",
                        )}
                      />
                      <span>
                        <span className="block text-sm font-semibold text-[#0F1115]">
                          {r.label}
                        </span>
                        <span className="block text-xs text-[#6B7280]">
                          {r.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
                {editingOwner && (
                  <p className="text-xs text-[#6B7280]">{tr("The account owner always keeps the Administration role.")}</p>
                )}
                {errors.role && (
                  <p className="text-[11px] font-medium text-[#9B0000]">
                    {errors.role}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Email notifications */}
          <div className="space-y-3 border-t border-[#F0F2F5] pt-4">
            <SectionLabel
              subtitle={tr("Get an email when something needs attention.")}
              right={
                <Switch
                  checked={form.receive_notifications}
                  onCheckedChange={(v) => set("receive_notifications", !!v)}
                  data-testid="add-user-receive-notifications"
                />
              }
            >{tr("Email notifications")}</SectionLabel>
            {form.receive_notifications && (
              <div
                data-testid="add-user-channels"
                className="grid grid-cols-1 gap-1 rounded-lg border border-[#F0F2F5] p-2 sm:grid-cols-3"
              >
                {NOTIFICATION_CHANNELS.map((c) => {
                  const checked = form.notification_prefs.includes(c.value);
                  return (
                    <label
                      key={c.value}
                      data-testid={`channel-${c.value}`}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm",
                        checked ? "bg-[#FFEFEF]" : "hover:bg-[#FAFAFB]",
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleChannel(c.value)}
                        data-testid={`channel-${c.value}-input`}
                      />
                      {c.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Marketing emails */}
          <div className="flex items-start justify-between gap-3 border-t border-[#F0F2F5] pt-4">
            <div>
              <p className="text-sm font-semibold text-[#0F1115]">{tr("Marketing emails")}</p>
              <p className="mt-0.5 text-xs text-[#6B7280]">{tr("Receive product news, tips and updates. You can unsubscribe anytime.")}</p>
            </div>
            <Switch
              checked={form.marketing_emails}
              onCheckedChange={(v) => set("marketing_emails", !!v)}
              data-testid="add-user-marketing"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse items-stretch justify-end gap-2 border-t border-[#EFE4E4] px-5 py-4 sm:flex-row sm:items-center sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="add-user-cancel"
            className="inline-flex h-10 items-center justify-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115] disabled:opacity-60"
          >{tr("Cancel")}</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            data-testid="add-user-submit"
            className="humrahe-btn-primary inline-flex h-10 items-center justify-center rounded-md px-5 text-sm font-semibold disabled:opacity-60"
          >
            {busy ? tr("Saving…") : isEdit ? tr("Save changes") : tr("Send invite")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserModal;
